import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import type { ProductApis } from '@flipster/apis/v1/dynamic-earn';
import { getDefiYieldsOverviewApi, narsilApis } from '@flipster/apis/v1/dynamic-earn';
import type { CumulativeEarningsHistoryItem, Strategy } from '@flipster/apis/v1/dynamic-earn/types';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { NrsusdtpCurrency, UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { v1CurrenciesBunja } from '../../shared/currency/use-v1-currencies';
import { buildChartData } from './chart-data';
import { getEstimatedHours } from './date';

interface ProductOverviewBunjaConfig {
  queryKey: string;
  apis: ProductApis;
  currency: string;
}

function createProductOverviewBunja(config: ProductOverviewBunjaConfig) {
  const overviewBunja = bunja(() => {
    const overviewBaseAtom = atomWithQuery(() => ({
      queryKey: [config.queryKey],
      queryFn: config.apis.getOverview,
      refetchOnMount: 'always',
      select: (res) => res.data,
    }));
    const overviewAtom = atom((get) => get(overviewBaseAtom).data);
    const isPendingAtom = atom((get) => get(overviewBaseAtom).isPending);

    const isFirstTimeUserAtom = atom((get) => get(overviewAtom)?.isFirstTimeUser);
    const hasSubscribedBalanceAtom = atom((get) => Num(get(overviewAtom)?.subscribedBalance ?? '0').gte('0.01'));
    const hasPendingRequestAtom = atom((get) => (get(overviewAtom)?.processingMintRequests ?? []).length > 0);

    const { currenciesKeyByCurrencyAtom } = bunja.use(v1CurrenciesBunja);
    const usdtCollateralRateAtom = atom((get) => {
      const currenciesKeyByCurrency = get(currenciesKeyByCurrencyAtom);
      const usdtCurrency = currenciesKeyByCurrency?.[UsdtCurrency];
      return Num(usdtCurrency?.collateralRate ?? '0');
    });
    const productCollateralRateAtom = atom((get) => {
      const currenciesKeyByCurrency = get(currenciesKeyByCurrencyAtom);
      const productCurrency = currenciesKeyByCurrency?.[config.currency];
      return Num(productCurrency?.collateralRate ?? '0');
    });

    return {
      overviewAtom,
      isPendingAtom,
      isFirstTimeUserAtom,
      hasSubscribedBalanceAtom,
      hasPendingRequestAtom,
      usdtCollateralRateAtom,
      productCollateralRateAtom,
    };
  });
  overviewBunja.debugLabel = `productOverviewBunja(${config.queryKey})`;
  return overviewBunja;
}

export type ProductOverviewBunja = ReturnType<typeof createProductOverviewBunja>;

function createProductDetailsBunja(apis: ProductApis, queryKey: string) {
  const detailsBunja = bunja(() => {
    const detailsBaseAtom = atomWithQuery(() => ({
      queryKey: [queryKey],
      queryFn: apis.getDetails,
      select: (res) => res.data,
    }));
    const detailsAtom = atom((get) => get(detailsBaseAtom).data);
    const sortedStrategiesAtom = atom<Strategy[]>((get) => {
      const strategies = get(detailsAtom)?.portfolioBreakdown?.strategies ?? [];
      return strategies.sort((a, b) => Num(b.allocationPercentage).comparedTo(Num(a.allocationPercentage)));
    });
    return { detailsAtom, sortedStrategiesAtom };
  });
  detailsBunja.debugLabel = `productDetailsBunja(${queryKey})`;
  return detailsBunja;
}

export type ProductDetailsBunja = ReturnType<typeof createProductDetailsBunja>;

function createMintOverviewBunja(apis: ProductApis, queryKey: string, walletCurrency: string) {
  const mintBunja = bunja(() => {
    const { walletsAtom } = bunja.use(walletsBunja);

    const mintOverviewBaseAtom = atomWithQuery(() => ({
      queryKey: [queryKey],
      queryFn: apis.getMintOverview,
      select: (res) => res.data,
    }));
    const mintOverviewAtom = atom((get) => get(mintOverviewBaseAtom).data);
    const mintOverviewPendingAtom = atom((get) => get(mintOverviewBaseAtom).isPending);
    const minAmountAtom = atom((get) => Num(get(mintOverviewAtom)?.minAmount ?? '10.00').toFixed(2));
    const availableBalanceAtom = atom((get) =>
      Num(get(walletsAtom)?.[walletCurrency]?.walletBalanceExclLockedInUsdt ?? '0'),
    );
    const hasAvailableBalanceAtom = atom((get) => get(availableBalanceAtom).gte('0.01'));
    const hasSubscribableBalanceAtom = atom((get) => get(availableBalanceAtom).gte(get(minAmountAtom)));
    const estimatedProcessingHoursAtom = atom((get) => {
      const mintOverview = get(mintOverviewAtom);
      return getEstimatedHours(mintOverview?.estimatedCompletedAt);
    });

    return {
      mintOverviewAtom,
      mintOverviewPendingAtom,
      minAmountAtom,
      availableBalanceAtom,
      hasAvailableBalanceAtom,
      hasSubscribableBalanceAtom,
      estimatedProcessingHoursAtom,
    };
  });
  mintBunja.debugLabel = `mintOverviewBunja(${queryKey})`;
  return mintBunja;
}

export type ProductMintOverviewBunja = ReturnType<typeof createMintOverviewBunja>;

function createRedeemOverviewBunja(apis: ProductApis, queryKey: string, walletCurrency: string) {
  const redeemBunja = bunja(() => {
    const { walletsAtom } = bunja.use(walletsBunja);

    const redeemOverviewBaseAtom = atomWithQuery(() => ({
      queryKey: [queryKey],
      queryFn: apis.getRedeemOverview,
      select: (res) => res.data,
    }));
    const redeemOverviewAtom = atom((get) => get(redeemOverviewBaseAtom).data);
    const redeemOverviewPendingAtom = atom((get) => get(redeemOverviewBaseAtom).isPending);
    const minAmountAtom = atom((get) => Num(get(redeemOverviewAtom)?.minAmount ?? '10.00').toFixed(2));
    const availableBalanceAtom = atom((get) =>
      Num(get(walletsAtom)?.[walletCurrency]?.walletBalanceExclLockedInUsdt ?? '0'),
    );
    const hasAvailableBalanceAtom = atom((get) => get(availableBalanceAtom).gte('0.01'));
    const hasRedeemableBalanceAtom = atom((get) => get(availableBalanceAtom).gte(get(minAmountAtom)));

    const instantRedeemInfoAtom = atom((get) => {
      const redeemOverview = get(redeemOverviewAtom);
      return redeemOverview?.redeemTypes.find((type) => type.type === 'INSTANT');
    });
    const instantLeftCapAtom = atom((get) => {
      const instantRedeemInfo = get(instantRedeemInfoAtom);
      return Num(instantRedeemInfo?.leftCap ?? '0');
    });
    const instantFeePercentageAtom = atom((get) => {
      const instantRedeemInfo = get(instantRedeemInfoAtom);
      return Num(instantRedeemInfo?.feeRate ?? '0')
        .multipliedBy(100)
        .toFixed(2);
    });
    const standardRedeemInfoAtom = atom((get) => {
      const redeemOverview = get(redeemOverviewAtom);
      return redeemOverview?.redeemTypes.find((type) => type.type === 'STANDARD');
    });
    const standardEstimatedProcessingHoursAtom = atom((get) => {
      const standardRedeemInfo = get(standardRedeemInfoAtom);
      const estimatedCompletedAt = standardRedeemInfo?.estimatedCompletedAt;
      return getEstimatedHours(estimatedCompletedAt);
    });
    const standardFeePercentageAtom = atom((get) => {
      const standardRedeemInfo = get(standardRedeemInfoAtom);
      return Num(standardRedeemInfo?.feeRate ?? '0')
        .multipliedBy(100)
        .toFixed(2);
    });
    const isInstantAvailableAtom = atom((get) => {
      const instantRedeemInfo = get(instantRedeemInfoAtom);
      const minAmount = get(minAmountAtom);
      return Num(instantRedeemInfo?.leftCap ?? '0').gt(minAmount) && instantRedeemInfo?.available;
    });

    return {
      redeemOverviewAtom,
      redeemOverviewPendingAtom,
      availableBalanceAtom,
      minAmountAtom,
      hasAvailableBalanceAtom,
      hasRedeemableBalanceAtom,
      standardEstimatedProcessingHoursAtom,
      standardFeePercentageAtom,
      instantFeePercentageAtom,
      standardRedeemInfoAtom,
      instantRedeemInfoAtom,
      instantLeftCapAtom,
      isInstantAvailableAtom,
    };
  });
  redeemBunja.debugLabel = `redeemOverviewBunja(${queryKey})`;
  return redeemBunja;
}

export type ProductRedeemOverviewBunja = ReturnType<typeof createRedeemOverviewBunja>;

// ─── Narsil ────────────────────────────────────────────

export const NarsilOverviewQueryKey = 'getNarsilOverviewApi';

export const narsilOverviewBunja = createProductOverviewBunja({
  queryKey: NarsilOverviewQueryKey,
  apis: narsilApis,
  currency: NrsusdtpCurrency,
});

export const narsilDetailsBunja = createProductDetailsBunja(narsilApis, 'getNarsilDetailsApi');
export const narsilMintOverviewBunja = createMintOverviewBunja(narsilApis, 'getNarsilMintOverviewApi', UsdtCurrency);
export const narsilRedeemOverviewBunja = createRedeemOverviewBunja(
  narsilApis,
  'getNarsilRedeemOverviewApi',
  NrsusdtpCurrency,
);

// ─── Aggregator Overview ────────────────────────────────

export const DefiYieldsOverviewQueryKey = 'getDefiYieldsOverviewApi';

export const defiYieldsOverviewBunja = bunja(() => {
  const overviewBaseAtom = atomWithQuery(() => ({
    queryKey: [DefiYieldsOverviewQueryKey],
    queryFn: getDefiYieldsOverviewApi,
    refetchOnMount: 'always',
    select: (res) => res.data,
  }));

  const overviewAtom = atom((get) => get(overviewBaseAtom).data);
  const isPendingAtom = atom((get) => get(overviewBaseAtom).isPending);

  const totalSubscribedAtom = atom((get) => get(overviewAtom)?.totalSubscribed ?? '0');
  const cumulativeEarningsAtom = atom((get) => get(overviewAtom)?.cumulativeEarnings ?? '0');

  const cumulativeEarningsHistoryAtom = atom<CumulativeEarningsHistoryItem[]>(
    (get) => get(overviewAtom)?.cumulativeEarningsHistory ?? [],
  );

  const isChartVisibleAtom = atom((get) => get(cumulativeEarningsHistoryAtom).length >= 9);

  const chartDataAtom = atom((get) => {
    const history = get(cumulativeEarningsHistoryAtom);
    const todayCumulativeEarnings = get(cumulativeEarningsAtom);
    return buildChartData(history, todayCumulativeEarnings);
  });

  const nrsusdtpAvailableAtom = atom((get) => get(overviewAtom)?.NRSUSDTP?.available ?? false);

  const productOrderAtom = atom((get) => get(overviewAtom)?.productOrder ?? []);

  const showOverviewAtom = atom((get) => {
    const overview = get(overviewAtom);
    const narsilIsFirstTimeUser = overview?.NRSUSDTP?.isFirstTimeUser ?? true;
    return !narsilIsFirstTimeUser;
  });

  return {
    overviewAtom,
    isPendingAtom,
    totalSubscribedAtom,
    cumulativeEarningsAtom,
    cumulativeEarningsHistoryAtom,
    isChartVisibleAtom,
    chartDataAtom,
    nrsusdtpAvailableAtom,
    productOrderAtom,
    showOverviewAtom,
  };
});
defiYieldsOverviewBunja.debugLabel = 'defiYieldsOverviewBunja';
